/**
 * EmbedScheduleCard — Pro-only dashboard widget that gives the owner
 * an iframe snippet to paste on their own website.
 *
 * Renders three states:
 *   - No published truck profile yet  → soft prompt to publish first
 *   - Pro + published profile          → snippet + copy button + preview link
 *   - Free + published profile         → upgrade nudge
 */
import { useEffect, useMemo, useState } from "react";
import { Code, Copy, CheckCircle, ArrowUpRight, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

const TT_BASE = "https://thetrucktrackr.com";

export default function EmbedScheduleCard({ user }) {
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reuse the profile-completeness endpoint which already returns
  // truck_slug + is_published for the current owner. Saves us a
  // dedicated backend round trip and keeps the data fresh.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/owner/profile-completeness")
      .then(({ data }) => {
        if (!cancelled) {
          setProfile(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfile(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isPro = !!user?.is_pro;
  const slug = profile?.truck_slug;
  // Profile-completeness only returns `truck_slug` after publishing —
  // so the presence of the slug already implies published. Belt-and-
  // suspenders: still check `is_published` if the endpoint surfaces it.
  const published = !!slug && profile?.is_published !== false;

  const snippet = useMemo(() => {
    if (!slug || !published) return "";
    // Standard iframe with sensible defaults. Owners can tweak width /
    // height; we use 100% width by default so it adapts to any column.
    return `<iframe src="${TT_BASE}/embed/truck/${slug}/week" width="100%" height="520" frameborder="0" style="border:0;border-radius:12px;max-width:760px;" loading="lazy" title="TruckTrackr schedule"></iframe>`;
  }, [slug, published]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed code copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  // --- State: loading or no published profile yet ----------------------
  if (loading) {
    return null;
  }
  if (!profile || !published || !slug) {
    return (
      <section
        className="rounded-2xl border border-border bg-card p-5"
        data-testid="embed-card-no-profile"
      >
        <div className="flex items-start gap-3">
          <div className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10 shrink-0">
            <Code weight="duotone" className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
              Embed Your Schedule
            </div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              Publish your truck profile first
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Once your public profile is published, you&apos;ll get a one-line
              embed code here that puts your live TruckTrackr schedule on
              your own website.
            </p>
          </div>
        </div>
      </section>
    );
  }

  // --- State: published but not Pro --------------------------------------
  if (!isPro) {
    return (
      <section
        className="rounded-2xl border border-border bg-card p-5"
        data-testid="embed-card-gated"
      >
        <div className="flex items-start gap-3">
          <div className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10 shrink-0">
            <Lock weight="duotone" className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
              Embed Your Schedule · Pro
            </div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              Put your live schedule on your own website
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Pro members get a paste-once embed code — your customers
              see your TruckTrackr schedule right on your homepage,
              auto-updating as you post events.
            </p>
            <a
              href="/upgrade"
              className="mt-4 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-sm h-9 px-4 transition"
              data-testid="embed-card-upgrade-cta"
            >
              Upgrade to Pro
              <ArrowUpRight weight="bold" className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>
    );
  }

  // --- State: Pro + published — show the snippet -------------------------
  const previewUrl = `${TT_BASE}/embed/truck/${slug}/week`;

  return (
    <section
      className="rounded-2xl border border-border bg-card overflow-hidden"
      data-testid="embed-card"
    >
      <div className="px-5 py-4 border-b border-border flex items-center gap-3 bg-gradient-to-r from-primary/5 to-transparent">
        <div className="grid place-items-center h-10 w-10 rounded-xl bg-primary/10">
          <Code weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
            Embed Your Schedule
          </div>
          <div className="font-display font-bold text-lg leading-tight">
            Paste this on your website
          </div>
        </div>
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1"
          data-testid="embed-card-preview"
        >
          Preview
          <ArrowUpRight weight="bold" className="h-3 w-3" />
        </a>
      </div>

      <div className="p-5 space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Set it up once. As you post events, your website&apos;s embed
          updates automatically — no copy-paste, no re-deploy.
        </p>
        <div className="relative">
          <textarea
            readOnly
            value={snippet}
            onClick={(e) => e.currentTarget.select()}
            className="w-full font-mono text-[11px] bg-muted/40 border border-border rounded-lg p-3 pr-12 leading-relaxed h-24 resize-none text-foreground/80"
            data-testid="embed-card-snippet"
          />
          <button
            type="button"
            onClick={copy}
            className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold px-2.5 py-1.5 transition"
            data-testid="embed-card-copy-btn"
          >
            {copied ? (
              <>
                <CheckCircle weight="fill" className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy weight="bold" className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Works on Squarespace, Wix, GoDaddy, WordPress, raw HTML — anywhere
          you can drop an iframe. Adjust the <code>height</code> attribute
          if your layout needs more or less room.
        </p>
      </div>
    </section>
  );
}
